import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useYacht } from "../../context/YachtContext";
import { useAuth } from "../../context/AuthContext";
import { api, fmtErr, API } from "../../lib/api";
import { PageHeader } from "./Dashboard";
import {
  Search, UsersRound, UserPlus, Ship, Briefcase, Phone, Mail,
  Plus, Send, Pencil, Trash2, Building2, User as UserIcon, Star, MessageSquare,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import AttachmentsPanel from "../../components/AttachmentsPanel";
import { useTour } from "../../components/tour/TourContext";
import TourReplayButton from "../../components/tour/TourReplayButton";
import { toast } from "sonner";
import {
  SUPPLIER_CATEGORY_LABELS as CATEGORY_LABELS_SHARED,
} from "../../lib/supplierCategories";
import {
  RANK, ROLE_LABELS, rolesAllowedFor, emptyForm,
  SupBizField, SupplierHistory, BizRow, BizAddress,
} from "./network/helpers";
import { SupplierFormDialog } from "./network/SupplierFormDialog";

const CATEGORY_LABELS = CATEGORY_LABELS_SHARED;

export default function Network({ section = "all" }) {
  // Pending supplier reviews (payment-confirmed jobs not yet rated).
  const { user } = useAuth();
  const { yachts, activeYacht } = useYacht();
  const navigate = useNavigate();

  // Direct message the supplier's linked contact from the Info popup —
  // thread is scoped to a yacht both parties share (prefer the active one).
  const messageSupplier = async (s) => {
    const yids = (s.yachts || []).map((y) => y.id);
    const yachtId = yids.includes(activeYacht?.id) ? activeYacht.id : yids[0];
    if (!s.user_id || !yachtId) return;
    try {
      const { data } = await api.post("/direct-threads", { user_id: s.user_id, yacht_id: yachtId });
      navigate(`/app/messages?dm=${data.id}`);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const { activeTour } = useTour();
  const [known, setKnown] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [roleFilter, setRoleFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("all"); // all | online | offline

  // Crew assign-to-yacht modal
  const [target, setTarget] = useState(null);
  const [pickYacht, setPickYacht] = useState("");
  const [pickRole, setPickRole] = useState("");
  const [pickedMembers, setPickedMembers] = useState([]);
  const [busy, setBusy] = useState(false);

  // Supplier form/edit modal
  const [supForm, setSupForm] = useState(null); // null | {mode:'create'|'edit', data}
  const [supBusy, setSupBusy] = useState(false);

  // Supplier settings (assign yachts, send invite)
  const [supSettings, setSupSettings] = useState(null);

  // Crew "Info" modal (read-only view of a person in My network)
  const [crewInfo, setCrewInfo] = useState(null);
  const [crewInfoBusy, setCrewInfoBusy] = useState(false);

  // Supplier "Add to yacht" modal — pick a yacht, fire /suppliers/{id}/invite
  // which both assigns and sends the onboarding email. The companion handlers
  // are defined below `assignableYachts` so they can close over it without TDZ.
  const [supplierAssignTarget, setSupplierAssignTarget] = useState(null);
  const [supplierAssignYacht, setSupplierAssignYacht] = useState("");
  const [supplierAssignBusy, setSupplierAssignBusy] = useState(false);

  // Unified confirm dialog state (replaces window.confirm — needed because
  // the Emergent preview iframe suppresses native browser dialogs).
  const [confirm, setConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const runConfirm = async () => {
    if (!confirm?.action) return;
    setConfirmBusy(true);
    try {
      await confirm.action();
      setConfirm(null);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setConfirmBusy(false);
    }
  };

  // Senior on at least one yacht (per-yacht membership, NOT the account-level
  // role — after an ownership transfer the account role may lag behind).
  const canAssign = (yachts || []).some((y) =>
    ["owner", "manager", "captain", "first_officer"].includes(y.my_role));

  const load = async () => {
    setLoading(true);
    try {
      const [k, s] = await Promise.all([
        api.get("/users/known"),
        api.get("/suppliers"),
      ]);
      setKnown(k.data || []);
      setSuppliers(s.data || []);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Yachts the caller can assign someone TO
  const assignableYachts = useMemo(
    () => (yachts || []).filter((y) => ["owner", "manager", "captain", "first_officer"].includes(y.my_role)),
    [yachts],
  );

  // Supplier-assign handlers — defined here so they can close over
  // `assignableYachts` without hitting the temporal dead zone.
  const supplierAssignableForTarget = useMemo(() => {
    if (!supplierAssignTarget) return [];
    const onAlready = new Set((supplierAssignTarget.yachts || []).map((y) => y.id));
    return assignableYachts.filter((y) => !onAlready.has(y.id));
  }, [supplierAssignTarget, assignableYachts]);

  const openSupplierAssign = (s) => {
    setSupplierAssignTarget(s);
    const onAlready = new Set((s.yachts || []).map((y) => y.id));
    const first = assignableYachts.find((y) => !onAlready.has(y.id));
    setSupplierAssignYacht(first?.id || "");
  };

  const closeSupplierAssign = () => {
    setSupplierAssignTarget(null);
    setSupplierAssignYacht("");
    setSupplierAssignBusy(false);
  };

  const submitSupplierAssign = async () => {
    if (!supplierAssignTarget || !supplierAssignYacht) return;
    setSupplierAssignBusy(true);
    try {
      const yachtName = assignableYachts.find((y) => y.id === supplierAssignYacht)?.name;
      if (supplierAssignTarget.user_id) {
        // ONLINE supplier (already has a Seavanto account): adding them to a
        // new yacht should actually grant access — /invite creates the
        // provider yacht_members row AND emails them so the yacht shows up in
        // their dashboard. /assign alone would only make a silent address-book
        // link with no membership, which is the bug we're fixing here.
        await api.post(
          `/suppliers/${supplierAssignTarget.id}/invite`,
          { yacht_id: supplierAssignYacht },
        );
        toast.success(`${supplierAssignTarget.name} invited to ${yachtName} — they'll get an email and see it once they accept.`);
      } else {
        // OFFLINE supplier: address-book link only. The user must explicitly
        // click "Send invite" on the supplier profile to onboard them.
        await api.post(
          `/suppliers/${supplierAssignTarget.id}/assign`,
          { yacht_id: supplierAssignYacht },
        );
        toast.success(`${supplierAssignTarget.name} added to ${yachtName} as an offline supplier.`);
      }
      closeSupplierAssign();
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSupplierAssignBusy(false);
    }
  };

  // Crew = everyone EXCEPT service providers (now surfaced as "Online
  // Suppliers" under the Suppliers group). Real crew only.
  const crewMembers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return known
      .filter((u) => u.role !== "provider")
      .filter((u) => (roleFilter === "all" ? true : u.role === roleFilter))
      .filter((u) =>
        !q ? true
          : (u.name || "").toLowerCase().includes(q) ||
            (u.email || "").toLowerCase().includes(q))
      .sort((a, b) => {
        const ra = RANK[a.role] ?? 0;
        const rb = RANK[b.role] ?? 0;
        if (rb !== ra) return rb - ra;
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [known, query, roleFilter]);

  // Provider-role crew = online suppliers that hold a Seavanto account.
  // They live under the Suppliers group's "Online Suppliers" subsection.
  const providerCrew = useMemo(() => {
    const q = query.trim().toLowerCase();
    return known
      .filter((u) => u.role === "provider")
      .filter((u) =>
        !q ? true
          : (u.name || "").toLowerCase().includes(q) ||
            (u.email || "").toLowerCase().includes(q) ||
            (u.company || "").toLowerCase().includes(q))
      .sort((a, b) => (a.company || a.name || "").localeCompare(b.company || b.name || ""));
  }, [known, query]);

  const _supplierMatches = (s, q) =>
    !q ? true
      : (s.name || "").toLowerCase().includes(q) ||
        (s.email || "").toLowerCase().includes(q) ||
        (s.phone || "").toLowerCase().includes(q) ||
        (s.contact_person || "").toLowerCase().includes(q);

  // Sort: "name" (A–Z, default) or "rating" (best-reviewed first; unrated
  // last, ties broken by review count then name).
  const [supplierSort, setSupplierSort] = useState("name");
  const _supplierCmp = (a, b) => {
    if (supplierSort === "rating") {
      const ra = Number(a.review_count) > 0 ? Number(a.average_rating) : -1;
      const rb = Number(b.review_count) > 0 ? Number(b.average_rating) : -1;
      if (rb !== ra) return rb - ra;
      const cb = (Number(b.review_count) || 0) - (Number(a.review_count) || 0);
      if (cb !== 0) return cb;
    }
    return (a.name || "").localeCompare(b.name || "");
  };

  // Online suppliers = linked supplier rows (have a Seavanto account).
  const onlineSuppliers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (suppliers || [])
      .filter((s) => s.user_id)
      .filter((s) => _supplierMatches(s, q))
      .sort(_supplierCmp);
    // eslint-disable-next-line
  }, [suppliers, query, supplierSort]);

  // Offline suppliers = address-book only (no account).
  const offlineSuppliers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (suppliers || [])
      .filter((s) => !s.user_id)
      .filter((s) => _supplierMatches(s, q))
      .sort(_supplierCmp);
    // eslint-disable-next-line
  }, [suppliers, query, supplierSort]);

  const onlineCount = providerCrew.length + onlineSuppliers.length;
  const supplierTotal = onlineCount + offlineSuppliers.length;

  const distinctRoles = useMemo(
    () => Array.from(new Set(known.map((u) => u.role)))
      .filter((r) => r !== "provider")
      .sort((a, b) => (RANK[b] ?? 0) - (RANK[a] ?? 0)),
    [known],
  );

  // ───── Crew assign modal logic (preserved from previous version) ─────
  const openAssign = (u) => {
    setTarget(u);
    // Seed the yacht picker with the first yacht the person isn't on yet.
    const onAlready = new Set(u.yacht_ids || []);
    const first = assignableYachts.find((y) => !onAlready.has(y.id));
    setPickYacht(first?.id || "");
    setPickedMembers([]);
    setPickRole("");
  };

  const closeAssign = () => {
    setTarget(null);
    setPickYacht("");
    setPickRole("");
    setPickedMembers([]);
    setBusy(false);
  };

  useEffect(() => {
    let alive = true;
    if (!target || !pickYacht) {
      setPickedMembers([]);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/yachts/${pickYacht}/members`);
        if (alive) setPickedMembers(data || []);
      } catch {
        if (alive) setPickedMembers([]);
      }
    })();
    return () => { alive = false; };
  }, [target, pickYacht]);

  const pickedYachtObj = assignableYachts.find((y) => y.id === pickYacht);
  const existingRolesOnPicked = useMemo(
    () => new Set(pickedMembers.map((m) => m.role)),
    [pickedMembers],
  );
  const allowedRolesOnPicked = useMemo(
    () => (pickedYachtObj ? rolesAllowedFor(pickedYachtObj.my_role, existingRolesOnPicked) : []),
    [pickedYachtObj, existingRolesOnPicked],
  );

  useEffect(() => {
    if (!target || !allowedRolesOnPicked.length) {
      if (pickRole) setPickRole("");
      return;
    }
    if (allowedRolesOnPicked.includes(pickRole)) return;
    const preferred = target.role && allowedRolesOnPicked.includes(target.role)
      ? target.role
      : allowedRolesOnPicked.find((r) => (RANK[r] ?? 0) <= (RANK[target.role] ?? 100))
        || allowedRolesOnPicked[allowedRolesOnPicked.length - 1];
    setPickRole(preferred);
    // eslint-disable-next-line
  }, [target, allowedRolesOnPicked.join("|")]);

  const submitAssign = async () => {
    if (!target || !pickYacht || !pickRole) return;
    setBusy(true);
    try {
      await api.post("/users/invite", {
        yacht_id: pickYacht, email: target.email, role: pickRole,
      });
      const yachtName = assignableYachts.find((y) => y.id === pickYacht)?.name;
      toast.success(`${target.name} added to ${yachtName} as ${ROLE_LABELS[pickRole]}`);
      closeAssign();
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  // ───── Supplier create / edit ─────
  const openCreateSupplier = () => setSupForm({ mode: "create", data: emptyForm() });
  const openEditSupplier = (s, readOnly = false) => setSupForm({
    mode: "edit",
    readOnly,
    data: {
      type: "company",
      name: s.name || "",
      contact_person: s.contact_person || "",
      email: s.email || "",
      phone: s.phone || "",
      categories: s.categories || (s.category ? [s.category] : []),
      category: s.category || "",
      notes: s.notes || "",
      // Business details — canonical names (match marketplace Company details).
      website: s.website || "",
      vat_number: s.vat_number || "",
      tax_id: s.tax_id || "",
      bank_name: s.bank_name || "",
      bank_account_holder: s.bank_account_holder || "",
      bank_account_number: s.bank_account_number || "",
      bank_iban: s.bank_iban || "",
      bank_bic_swift: s.bank_bic_swift || "",
      address: s.address || "",
      city: s.city || "",
      postcode: s.postcode || "",
      country: s.country || "",
      id: s.id,
    },
  });

  const submitSupplier = async () => {
    const d = supForm.data;
    if (!d.name?.trim() || !d.email?.trim() || !d.phone?.trim()) {
      toast.error("Name, email and phone are required.");
      return;
    }
    setSupBusy(true);
    try {
      // Normalise: trim every string field and convert empty business
      // details to null so the backend stores them as cleared rather
      // than zero-length strings. Required fields stay as strings.
      const REQUIRED = new Set(["name", "email", "phone", "type"]);
      const payload = { ...d };
      Object.keys(payload).forEach((k) => {
        if (typeof payload[k] === "string") {
          const trimmed = payload[k].trim();
          payload[k] = REQUIRED.has(k) ? trimmed : (trimmed || null);
        }
      });
      if (supForm.mode === "create") {
        const { data: created } = await api.post("/suppliers", payload);
        if (created?.existing) {
          toast.info(`Linked to existing supplier "${created.name}" — no duplicate created.`);
        } else {
          toast.success("Supplier added");
        }
      } else {
        await api.patch(`/suppliers/${d.id}`, payload);
        toast.success("Supplier updated");
      }
      setSupForm(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSupBusy(false);
    }
  };

  const deleteSupplier = (s) => {
    setConfirm({
      title: `Delete supplier "${s.name}"?`,
      description: "This removes them from all yachts they're assigned to. This cannot be undone.",
      confirmLabel: "Yes, delete",
      destructive: true,
      action: async () => {
        await api.delete(`/suppliers/${s.id}`);
        toast.success("Supplier deleted");
        load();
      },
    });
  };

  // ───── Remove crew from network (caller's address book only) ─────
  const removeFromNetwork = (u) => {
    setConfirm({
      title: `Remove ${u.name || u.email} from your network?`,
      description: "You can re-invite them by email anytime. Their account on Seavanto is not affected unless they were a never-accepted invite placeholder.",
      confirmLabel: "Yes, remove",
      destructive: true,
      action: async () => {
        const { data } = await api.delete(`/users/known/${u.id}`);
        toast.success(
          data?.user_deleted
            ? `${u.name || u.email} removed (placeholder account cleaned up).`
            : `${u.name || u.email} removed from your network.`,
        );
        load();
      },
    });
  };

  // ───── Supplier settings (assign yachts + send invite) ─────
  // For ONLINE suppliers (linked to a marketplace/user account) we silently
  // re-poll their latest company profile from the source on open, so the
  // displayed details (incl. categories) always match what the supplier
  // maintains. Falls back to the cached row if the refresh fails.
  const openSettings = async (s) => {
    setSupSettings({ ...s });
    if (!s.user_id) return;
    try {
      const { data } = await api.post(`/suppliers/${s.id}/sync-profile`);
      setSupSettings((cur) => (cur && cur.id === data.id ? data : cur));
      setSuppliers((prev) => prev.map((x) => (x.id === data.id ? data : x)));
    } catch {
      /* keep cached row — owner may lack sync permission */
    }
  };

  // Deep-link: ?supplier=<card_id> auto-opens that supplier's Info popup
  // (used by the Active-yacht Online Supplier rows).
  useEffect(() => {
    const sid = searchParams.get("supplier");
    if (!sid || loading) return;
    const card = suppliers.find((x) => x.id === sid);
    if (card) openSettings(card);
    const p = new URLSearchParams(searchParams);
    p.delete("supplier");
    setSearchParams(p, { replace: true });
  }, [loading, suppliers]); // eslint-disable-line react-hooks/exhaustive-deps

  // ───── Crew "Info" modal ─────
  const openCrewInfo = (u) => { setCrewInfo({ ...u }); setCrewEdit(null); };

  // When crewEdit is non-null, the Business-details block inside the
  // Crew Info modal switches to editable. Stores the dirty draft so
  // Reset/Save have clear semantics. The backend gate (owner/manager
  // of a shared yacht only) still applies — captains who try to save
  // will get a 403 surfaced as a toast.
  const [crewEdit, setCrewEdit] = useState(null); // null | dict of editable biz fields
  const [crewEditBusy, setCrewEditBusy] = useState(false);
  const startCrewEdit = () => {
    if (!crewInfo) return;
    const biz = crewInfo.business || {};
    setCrewEdit({
      website: biz.website || "",
      vat_id: biz.vat_id || "",
      tax_id: biz.tax_id || "",
      bank_name: biz.bank_name || "",
      account_holder: biz.account_holder || "",
      iban: biz.iban || "",
      bic_swift: biz.bic_swift || "",
      address_line1: biz.address_line1 || "",
      address_line2: biz.address_line2 || "",
      city: biz.city || "",
      postcode: biz.postcode || "",
      country: biz.country || "",
    });
  };
  const saveCrewEdit = async () => {
    if (!crewInfo || !crewEdit) return;
    setCrewEditBusy(true);
    try {
      const payload = {};
      Object.keys(crewEdit).forEach((k) => {
        const v = (crewEdit[k] || "").trim();
        payload[k] = v || null;
      });
      const { data: biz } = await api.patch(
        `/crew-members/${crewInfo.id}/business-details`,
        payload,
      );
      setCrewInfo({ ...crewInfo, business: Object.fromEntries(
        Object.entries(biz).filter(([_, v]) => v),
      ) });
      setCrewEdit(null);
      toast.success("Business details saved");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setCrewEditBusy(false);
    }
  };

  const refreshCrewInfo = async () => {
    if (!crewInfo) return;
    setCrewInfoBusy(true);
    try {
      // Re-fetch the network list (cheap; backend already filters by
      // visibility) and re-pick the freshest row for this user.
      const { data } = await api.get("/users/known");
      const fresh = (data || []).find((x) => x.id === crewInfo.id);
      if (fresh) {
        setCrewInfo(fresh);
        setKnown(data);
        toast.success("Refreshed");
      } else {
        // Person no longer in your network — close gracefully.
        toast.message("This person is no longer in your network");
        setCrewInfo(null);
        setKnown(data);
      }
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setCrewInfoBusy(false);
    }
  };

  const removeCrewFromNetworkInline = () => {
    if (!crewInfo) return;
    const u = crewInfo;
    setConfirm({
      title: `Remove ${u.name || u.email} from your network?`,
      description: "You can re-invite them by email anytime. Their Seavanto account is not affected.",
      confirmLabel: "Yes, remove",
      destructive: true,
      action: async () => {
        await api.delete(`/users/known/${u.id}`);
        toast.success(`${u.name || u.email} removed from your network.`);
        setCrewInfo(null);
        load();
      },
    });
  };

  const refreshSettings = async (sid) => {
    try {
      const { data } = await api.get(`/suppliers/${sid}`);
      setSupSettings(data);
      // Update in-list too
      setSuppliers((prev) => prev.map((x) => x.id === sid ? data : x));
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const syncSupplierProfile = () => {
    if (!supSettings) return;
    setConfirm({
      title: "Overwrite this supplier with their account data?",
      description: "All supplier fields — name, contact, email, phone, website, VAT, address and bank details — will be REPLACED with whatever the supplier user has set in their own Seavanto profile. Your internal Notes on this supplier are preserved. This cannot be undone — continue?",
      confirmLabel: "Yes, overwrite",
      destructive: true,
      action: async () => {
        const updated = await api.post(`/suppliers/${supSettings.id}/sync-profile`);
        toast.success("Supplier card overwritten with their latest profile");
        setSupSettings(updated.data);
        setSuppliers((prev) => prev.map((x) => x.id === updated.data.id ? updated.data : x));
      },
    });
  };

  // Remove the supplier from a SPECIFIC yacht (per-yacht detach). Mirrors
  // the per-yacht detach in the crew/network table. Leaves the supplier
  // in the user's network if they're still assigned to other yachts.
  const unassignFromYacht = (yacht) => {
    if (!supSettings) return;
    setConfirm({
      title: `Remove ${supSettings.name} from ${yacht.name}?`,
      description: "The supplier stays in your network. They can be added back to this yacht later.",
      confirmLabel: "Yes, remove",
      destructive: true,
      action: async () => {
        await api.delete(`/suppliers/${supSettings.id}/assign/${yacht.id}`);
        toast.success(`Removed from ${yacht.name}`);
        // Re-fetch the supplier so its yachts list and the supplier
        // list view both reflect the change.
        await refreshSettings(supSettings.id);
        load();
      },
    });
  };

  // Send the supplier an invite to become a LINKED Service Provider on
  // the platform. Until they accept they're still offline. Once their
  // user_id is populated the supplier card automatically becomes the
  // read-only "linked" view (Refresh-from-user available, edit hidden
  // for the locally-stored fields that now come from the user account).
  const sendSupplierInvite = (yacht) => {
    if (!supSettings) return;
    if (!supSettings.email) {
      toast.error("This supplier has no email on file — add one via Edit first.");
      return;
    }
    setConfirm({
      title: `Invite ${supSettings.name} to join Seavanto?`,
      description: `We'll email an invite linked to ${yacht.name}. Once they accept they become a linked Online Supplier and can interact with jobs assigned to them.`,
      confirmLabel: "Yes, send invite",
      action: async () => {
        await api.post(`/suppliers/${supSettings.id}/invite`, { yacht_id: yacht.id });
        toast.success(`Invite email sent to ${supSettings.email}`);
        await refreshSettings(supSettings.id);
        load();
      },
    });
  };

  // Yachts the assign-modal can offer for the current target. We exclude
  // yachts the person is already on (avoids the no-op "assign to a yacht
  // they're already on" picker option).
  const targetAssignableYachts = useMemo(() => {
    if (!target) return assignableYachts;
    const onAlready = new Set(target.yacht_ids || []);
    return assignableYachts.filter((y) => !onAlready.has(y.id));
  }, [target, assignableYachts]);

  // Shared row renderers — used by the Crew list and (for provider rows)
  // the Suppliers > Online Suppliers subsection so both stay in sync.
  const renderCrewRow = (u) => {
    const initials = (u.name || u.email || "U").slice(0, 2).toUpperCase();
    const clickable = u.role !== "provider";
    return (
      <li
        key={u.id}
        className={`px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 ${clickable ? "cursor-pointer hover:bg-zinc-50 transition-colors" : ""}`}
        data-testid={`network-row-${u.id}`}
        onClick={clickable ? () => openCrewInfo(u) : undefined}
        title={clickable ? "Open HR record" : undefined}
      >
        <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
          <Avatar className="h-[50px] w-[50px] shrink-0">
            <AvatarImage src={u.profile_photo ? `${API}/files/${u.profile_photo}` : u.picture} data-testid={`network-photo-${u.id}`} />
            <AvatarFallback className="bg-zinc-200 text-zinc-700 text-xs">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">
              {u.role === "provider" && u.company ? u.company : u.name}
            </div>
            {u.role === "provider" && u.company && u.name && (
              <div className="text-xs text-zinc-700 truncate" data-testid={`crew-contact-${u.id}`}>
                {u.name}
              </div>
            )}
            <div className="text-xs text-zinc-500 truncate">{u.email}</div>
            {u.phone && (
              <div className="text-xs text-zinc-500 truncate" data-testid={`crew-phone-${u.id}`}>
                {u.phone}
              </div>
            )}
            {u.yachts?.length > 0 ? (
              <div className="text-[11px] text-zinc-400 truncate flex items-center gap-1 mt-0.5">
                <Ship className="h-3 w-3 shrink-0" />
                On: {u.yachts.join(", ")}
              </div>
            ) : (
              <div className="text-[11px] text-zinc-400 mt-0.5">No current yacht together</div>
            )}
            {/* HR alert chips moved to the Active yacht tab (Users page) */}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap pl-12 sm:pl-0 shrink-0" onClick={(e) => e.stopPropagation()}>
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-zinc-200">
            {ROLE_LABELS[u.role] || u.role}
          </span>
          {canAssign && assignableYachts.some((y) => !(u.yacht_ids || []).includes(y.id)) && (
            <Button
              size="sm"
              className="bg-ink hover:bg-black"
              onClick={() => openAssign(u)}
              data-testid={`assign-${u.id}`}
            >
              <UserPlus className="h-3.5 w-3.5 mr-1.5" /> Add to yacht
            </Button>
          )}
          {u.role === "provider" ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => openCrewInfo(u)}
              data-testid={`crew-info-${u.id}`}
            >
              Info
            </Button>
          ) : (
            (u.yachts || []).length === 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => removeFromNetwork(u)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid={`crew-delete-${u.id}`}
                title="Remove from your network"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )
          )}
        </div>
      </li>
    );
  };

  const renderSupplierRow = (s) => {
    const initials = (s.name || s.email || "S").slice(0, 2).toUpperCase();
    const TypeIcon = s.type === "individual" ? UserIcon : Building2;
    return (
      <li
        key={s.id}
        className="px-4 py-3 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3"
        data-testid={`supplier-row-${s.id}`}
      >
        <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
          <Avatar className="h-9 w-9 shrink-0">
            <AvatarFallback className="bg-zinc-100 text-zinc-700 text-xs">
              <TypeIcon className="h-4 w-4" />
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate flex items-center gap-2">
              {s.name || "(unnamed)"}
              {s.user_id ? (
                <span
                  className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
                  data-testid={`supplier-linked-${s.id}`}
                >
                  Online
                </span>
              ) : (
                <span
                  className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 border border-zinc-200"
                  data-testid={`supplier-offline-${s.id}`}
                >
                  Offline
                </span>
              )}
              {Number(s.review_count) > 0 && (
                <span
                  className="inline-flex items-center gap-0.5 text-xs text-amber-600 font-medium shrink-0"
                  data-testid={`supplier-rating-${s.id}`}
                >
                  <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                  {Number(s.average_rating).toFixed(1)}
                  <span className="text-zinc-400 font-normal">({s.review_count})</span>
                </span>
              )}
            </div>
            {s.contact_person && (
              <div className="text-xs text-zinc-700 truncate">
                {s.contact_person}
              </div>
            )}
            <div className="text-xs text-zinc-500 truncate flex items-center gap-1 mt-0.5">
              <Mail className="h-3 w-3 shrink-0" /> {s.email}
            </div>
            {s.phone && (
              <div className="text-xs text-zinc-500 truncate flex items-center gap-1">
                <Phone className="h-3 w-3 shrink-0" /> {s.phone}
              </div>
            )}
            {s.user_id && s.yachts?.length > 0 && (
              <div className="text-[11px] text-zinc-400 truncate flex items-center gap-1 mt-0.5">
                <Ship className="h-3 w-3 shrink-0" />
                On: {s.yachts.map((y) => y.name).join(", ")}
              </div>
            )}
            {(() => {
              const cats = (s.categories && s.categories.length ? s.categories : [s.category]).filter(Boolean);
              return cats.length ? (
                <div className="flex flex-wrap gap-1 mt-2" data-testid={`supplier-cats-${s.id}`}>
                  {cats.map((c) => (
                    <span key={c} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-zinc-200 text-zinc-600">
                      {CATEGORY_LABELS[c] || c}
                    </span>
                  ))}
                </div>
              ) : null;
            })()}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap pl-12 sm:pl-0 shrink-0">
          {canAssign && s.user_id && assignableYachts.some((y) => !(s.yachts || []).map((yy) => yy.id).includes(y.id)) && (
            <Button
              size="sm"
              className="bg-ink hover:bg-black"
              onClick={() => openSupplierAssign(s)}
              data-testid={`supplier-assign-${s.id}`}
            >
              <UserPlus className="h-3.5 w-3.5 mr-1.5" /> Add to yacht
            </Button>
          )}
          {canAssign && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => openSettings(s)}
              data-testid={`supplier-settings-${s.id}`}
            >
              Info
            </Button>
          )}
        </div>
      </li>
    );
  };

  // ────────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 lg:p-10 max-w-5xl">
      <PageHeader
        overline="People"
        title={section === "team" ? "My team" : section === "suppliers" ? "My suppliers" : "My crew & suppliers"}
        right={canAssign && section !== "team" ? (
          <div className="flex items-center gap-2">
            <TourReplayButton tourId="my-network" />
            <Button
              onClick={openCreateSupplier}
              className="bg-ink hover:bg-black"
              data-testid="add-supplier-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Supplier
            </Button>
          </div>
        ) : null}
      />

      <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
        {section === "team"
          ? "Crew you've worked with across Seavanto. Click anyone to open their HR record on the yacht you share."
          : section === "suppliers"
            ? "Your online and offline supplier address book. No invitation email is sent automatically — use Send invite in each supplier's Info panel to onboard them manually."
            : "Crew you've worked with across Seavanto, plus your online and offline supplier address book."}
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400" />
          <Input
            placeholder="Search by name, email or phone"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-8"
            data-testid="network-search"
          />
        </div>
        <select
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          data-testid="network-role-filter"
          style={{ display: section === "suppliers" ? "none" : undefined }}
        >
          <option value="all">All crew roles</option>
          {distinctRoles.map((r) => (
            <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
          ))}
        </select>
        <span
          className="text-xs text-zinc-500 ml-auto"
          data-testid="network-count"
        >
          {section === "team" ? `${crewMembers.length} crew`
            : section === "suppliers" ? `${supplierTotal} suppliers`
            : `${crewMembers.length} crew · ${supplierTotal} suppliers`}
        </span>
      </div>

      {/* ───── Crew section ───── */}
      {section !== "suppliers" && (
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-2">
          <UsersRound className="h-4 w-4 text-zinc-500" />
          <h2 className="font-display text-lg" data-testid="section-crew">Crew</h2>
          <span className="text-xs text-zinc-400">({crewMembers.length})</span>
        </div>
        <div className="rounded-md border border-zinc-200 bg-white overflow-hidden" data-testid="network-list">
          {loading && (
            <div className="p-8 text-center text-sm text-zinc-500">Loading your network…</div>
          )}
          {!loading && crewMembers.length === 0 && (
            <div className="p-8 text-center">
              <UsersRound className="h-7 w-7 mx-auto text-zinc-300" />
              <div className="text-sm text-zinc-500 mt-2 max-w-sm mx-auto">
                {known.length === 0
                  ? "Once you invite someone to a yacht, they'll appear here — even if you later delete that yacht."
                  : "No crew match your filters."}
              </div>
            </div>
          )}
          {!loading && crewMembers.length > 0 && (
            <ul className="divide-y divide-zinc-200">
              {crewMembers.map(renderCrewRow)}
            </ul>
          )}
        </div>
      </div>
      )}

      {/* ───── Suppliers section ───── */}
      {section !== "team" && (
      <div className="mt-8" data-testid="section-suppliers">
        <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-zinc-500" />
            <h2 className="font-display text-lg">Suppliers</h2>
            <span className="text-xs text-zinc-400">({supplierTotal})</span>
          </div>
          {/* Online / Offline quick filter + sort */}
          {supplierTotal > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex gap-1 rounded-md border border-zinc-200 bg-zinc-50 p-1 text-xs" data-testid="supplier-sort">
                {[
                  { k: "name", label: "A–Z" },
                  { k: "rating", label: "Top rated" },
                ].map((opt) => (
                  <button
                    key={opt.k}
                    type="button"
                    onClick={() => setSupplierSort(opt.k)}
                    className={`px-3 py-1 rounded inline-flex items-center gap-1 ${supplierSort === opt.k ? "bg-white shadow-sm font-medium text-zinc-900" : "text-zinc-600"}`}
                    data-testid={`supplier-sort-${opt.k}`}
                  >
                    {opt.k === "rating" && <Star className="h-3 w-3 fill-amber-400 text-amber-400" />}
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-1 rounded-md border border-zinc-200 bg-zinc-50 p-1 text-xs" data-testid="supplier-filter">
                {[
                  { k: "all", label: `All (${supplierTotal})` },
                  { k: "online", label: `Online (${onlineCount})` },
                  { k: "offline", label: `Offline (${offlineSuppliers.length})` },
                ].map((opt) => (
                  <button
                    key={opt.k}
                    type="button"
                    onClick={() => setSupplierFilter(opt.k)}
                    className={`px-3 py-1 rounded ${supplierFilter === opt.k ? "bg-white shadow-sm font-medium text-zinc-900" : "text-zinc-600"}`}
                    data-testid={`supplier-filter-${opt.k}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {!loading && supplierTotal === 0 && (
          <div className="rounded-md border border-zinc-200 bg-white p-8 text-center" data-testid="supplier-list">
            <Briefcase className="h-7 w-7 mx-auto text-zinc-300" />
            <div className="text-sm text-zinc-500 mt-2 max-w-sm mx-auto">
              {suppliers.length === 0 && providerCrew.length === 0
                ? "No suppliers yet. Add a paint yard, engine tech, broker or agent — they'll appear here and you can assign them to yachts."
                : "No suppliers match your search."}
            </div>
          </div>
        )}

        {/* Empty-state when a filter hides everything but data exists. */}
        {!loading && supplierTotal > 0 && (
          (supplierFilter === "online" && onlineCount === 0) ||
          (supplierFilter === "offline" && offlineSuppliers.length === 0)
        ) && (
          <div className="rounded-md border border-zinc-200 bg-white p-8 text-center text-sm text-zinc-500" data-testid="supplier-filter-empty">
            No {supplierFilter} suppliers.
          </div>
        )}

        {/* Online Suppliers — provider-role crew + linked supplier rows */}
        {!loading && supplierFilter !== "offline" && onlineCount > 0 && (
          <div className="mb-5">
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5" data-testid="suppliers-online-heading">
              Online Suppliers <span className="text-zinc-400">({onlineCount})</span>
            </div>
            <div className="rounded-md border border-zinc-200 bg-white overflow-hidden" data-testid="supplier-list-online">
              <ul className="divide-y divide-zinc-200">
                {providerCrew.map(renderCrewRow)}
                {onlineSuppliers.map(renderSupplierRow)}
              </ul>
            </div>
          </div>
        )}

        {/* Offline Suppliers — address-book only, alphabetical */}
        {!loading && supplierFilter !== "online" && offlineSuppliers.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5" data-testid="suppliers-offline-heading">
              Offline Suppliers <span className="text-zinc-400">({offlineSuppliers.length})</span>
            </div>
            <div className="rounded-md border border-zinc-200 bg-white overflow-hidden" data-testid="supplier-list-offline">
              <ul className="divide-y divide-zinc-200">
                {offlineSuppliers.map(renderSupplierRow)}
              </ul>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Crew assign modal */}
      <Dialog open={!!target} onOpenChange={(o) => { if (!o) closeAssign(); }}>
        <DialogContent className="max-w-md" data-testid="assign-modal">
          <DialogHeader>
            <DialogTitle>Add {target?.name} to a yacht</DialogTitle>
            <DialogDescription>
              {target?.email} will get a notification that you've added them. No new password needed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-zinc-600">Yacht</label>
              <select
                className="mt-1 h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                value={pickYacht}
                onChange={(e) => setPickYacht(e.target.value)}
                data-testid="assign-yacht"
              >
                {targetAssignableYachts.map((y) => (
                  <option key={y.id} value={y.id}>{y.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">Role on this yacht</label>
              <select
                className="mt-1 h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                value={pickRole}
                onChange={(e) => setPickRole(e.target.value)}
                data-testid="assign-role"
              >
                {allowedRolesOnPicked.map((r) => (
                  <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                ))}
              </select>
              <p className="text-[11px] text-zinc-500 mt-1">
                {pickedYachtObj?.my_role === "captain" && existingRolesOnPicked.has("manager")
                  ? "A manager is already on this yacht, so the manager slot isn't available."
                  : "You can assign any role at or below your rank on this yacht."}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeAssign} data-testid="assign-cancel">Cancel</Button>
            <Button
              onClick={submitAssign}
              disabled={busy || !pickYacht || !pickRole}
              className="bg-ink hover:bg-black"
              data-testid="assign-confirm"
            >
              {busy ? "Adding…" : "Add to yacht"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supplier create / edit modal */}
      <SupplierFormDialog
        supForm={supForm}
        setSupForm={setSupForm}
        submitSupplier={submitSupplier}
        supBusy={supBusy}
        activeTour={activeTour}
      />

      {/* Supplier Info modal — read-only details. Footer differs depending on
          whether the supplier is currently assigned to any yacht. */}
      <Dialog open={!!supSettings} onOpenChange={(o) => { if (!o) setSupSettings(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="supplier-settings-modal" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>
              {supSettings?.name}
              {supSettings?.user_id ? (
                <span
                  className="ml-2 align-middle text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
                  data-testid="supplier-linked-pill"
                >
                  Linked
                </span>
              ) : (
                <span
                  className="ml-2 align-middle text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 border border-zinc-200"
                  data-testid="supplier-offline-pill"
                >
                  Offline
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              {((supSettings?.categories && supSettings.categories.length ? supSettings.categories : [supSettings?.category]).filter(Boolean).map((c) => CATEGORY_LABELS[c] || c)).join(" · ")}
            </DialogDescription>
          </DialogHeader>

          {supSettings && (
            <div className="space-y-3 text-sm">
              {supSettings.contact_person && (
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500 w-24 shrink-0">Contact</span>
                  <span className="font-medium">{supSettings.contact_person}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Mail className="h-3.5 w-3.5 text-zinc-400" />
                <span className="text-zinc-500 w-20 shrink-0">Email</span>
                <span className="truncate">{supSettings.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-3.5 w-3.5 text-zinc-400" />
                <span className="text-zinc-500 w-20 shrink-0">Phone</span>
                <span className="truncate">{supSettings.phone}</span>
              </div>

              {supSettings.user_id && (
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1 mt-2">Assigned yachts</div>
                  {(supSettings.yachts || []).length === 0 ? (
                    <div className="text-zinc-400 text-xs">Not assigned to any yacht.</div>
                  ) : (
                    <ul className="space-y-1" data-testid="supplier-assigned-list">
                      {supSettings.yachts.map((y) => (
                        <li key={y.id} className="flex items-center gap-2 text-sm">
                          <Ship className="h-3.5 w-3.5 text-zinc-400" />
                          <span>{y.name}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {supSettings.notes && (
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1 mt-2">Notes</div>
                  <p className="text-zinc-700 whitespace-pre-wrap">{supSettings.notes}</p>
                </div>
              )}

              {supSettings.business && (
                <div data-testid="supplier-business">
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1 mt-2">Business details</div>
                  <div className="rounded-md border border-zinc-200 bg-zinc-50/60 p-3 space-y-1.5 text-sm">
                    {supSettings.business.website && (
                      <BizRow
                        k="Website"
                        v={(
                          <a
                            href={/^https?:\/\//i.test(supSettings.business.website)
                              ? supSettings.business.website
                              : `https://${supSettings.business.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-ink underline underline-offset-2 hover:text-black break-all"
                          >
                            {supSettings.business.website}
                          </a>
                        )}
                      />
                    )}
                    {supSettings.business.bank_account_holder && (
                      <BizRow k="Account holder" v={supSettings.business.bank_account_holder} />
                    )}
                    {supSettings.business.bank_name && (
                      <BizRow k="Bank" v={supSettings.business.bank_name} />
                    )}
                    {supSettings.business.bank_account_number && (
                      <BizRow k="Account number" v={supSettings.business.bank_account_number} mono copyable />
                    )}
                    {supSettings.business.bank_iban && (
                      <BizRow k="IBAN" v={supSettings.business.bank_iban} mono copyable />
                    )}
                    {supSettings.business.bank_bic_swift && (
                      <BizRow k="BIC / SWIFT" v={supSettings.business.bank_bic_swift} mono copyable />
                    )}
                    {supSettings.business.vat_number && (
                      <BizRow k="VAT number" v={supSettings.business.vat_number} copyable />
                    )}
                    {supSettings.business.tax_id && (
                      <BizRow k="Tax ID" v={supSettings.business.tax_id} />
                    )}
                    {(supSettings.business.address || supSettings.business.city || supSettings.business.country) && (
                      <BizAddress addr={supSettings.business} />
                    )}
                  </div>
                </div>
              )}

              {supSettings.invited_at && (
                <div className="text-[11px] text-zinc-500 pt-1">
                  Last invited: {new Date(supSettings.invited_at).toLocaleString()}
                </div>
              )}

              {/* Files attached to this supplier — visible only to
                  the uploader + owner/manager per the spec. The list
                  is read-only here; deletion happens in the Edit dialog. */}
              <div className="pt-1">
                <AttachmentsPanel kind="supplier" refId={supSettings.id} title="Files" />
              </div>

              {/* ───── Per-yacht assignments (ONLINE suppliers) ─────
                  Linked suppliers keep the full per-yacht management view:
                  Send invite onto a yacht they're not yet a member of, plus
                  Remove from a specific yacht. */}
              {supSettings.user_id && (supSettings.yachts || []).length > 0 && (
                <div data-testid="supplier-yacht-list" className="pt-1">
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                    Assigned to {supSettings.yachts.length === 1 ? "yacht" : "yachts"}
                  </div>
                  <ul className="space-y-1.5">
                    {supSettings.yachts.map((y) => (
                      <li key={y.id} className="flex items-center justify-between gap-3 rounded-md border border-zinc-200 px-3 py-2 text-sm">
                        <span className="font-medium text-zinc-800 truncate">{y.name}</span>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {/* Send invite shows for any assigned yacht this
                              linked supplier isn't yet a crew member of (e.g.
                              one just added via "Add to yacht"). */}
                          {!y.provider_member && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => sendSupplierInvite(y)}
                              data-testid={`supplier-invite-${y.id}`}
                              title="Invite supplier to become a linked Online Supplier on this yacht"
                              className="h-7 px-2 text-xs"
                            >
                              Send invite
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => unassignFromYacht(y)}
                            data-testid={`supplier-unassign-${y.id}`}
                            className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Remove from this yacht (supplier stays in network)"
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1" />Remove
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* ───── Invite to a yacht (OFFLINE suppliers) ─────
                  Offline suppliers are address-book contacts — they are NOT
                  pre-assigned to yachts. To onboard them, the senior picks a
                  yacht and sends an invite, which links + emails them and
                  promotes the card to an Online Supplier (it then follows the
                  per-yacht flow above). No per-yacht assign/remove step. */}
              {!supSettings.user_id && (
                <div data-testid="supplier-invite-yachts" className="pt-1">
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Invite to a yacht</div>
                  {assignableYachts.length === 0 ? (
                    <div className="text-zinc-400 text-xs">You don't manage any yachts to invite this supplier to.</div>
                  ) : (
                    <ul className="space-y-1.5">
                      {assignableYachts.map((y) => (
                        <li key={y.id} className="flex items-center justify-between gap-3 rounded-md border border-zinc-200 px-3 py-2 text-sm">
                          <span className="font-medium text-zinc-800 truncate flex items-center gap-2">
                            <Ship className="h-3.5 w-3.5 text-zinc-400 shrink-0" />{y.name}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => sendSupplierInvite(y)}
                            data-testid={`supplier-invite-${y.id}`}
                            title={`Invite ${supSettings.name} to ${y.name} on Seavanto`}
                            className="h-7 px-2 text-xs"
                          >
                            Send invite
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="text-[11px] text-zinc-500 mt-2">
                    Sending an invite emails the supplier and moves them to Online Suppliers.
                  </p>
                </div>
              )}

              <SupplierHistory supplierId={supSettings.id} />
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:justify-between">
            <div className="flex gap-2 order-2 sm:order-1">
              {(supSettings?.yachts || []).length === 0 && (
                <Button
                  variant="ghost"
                  onClick={() => { const s = supSettings; setSupSettings(null); deleteSupplier(s); }}
                  className="text-red-600 hover:bg-red-50"
                  data-testid="supplier-delete"
                >
                  <Trash2 className="h-4 w-4 mr-1.5" /> Remove from network
                </Button>
              )}
            </div>
            <div className="flex gap-2 order-1 sm:order-2 flex-wrap">
              {/* Online suppliers maintain their own company profile, so we
                  only let owners VIEW the polled details (+ edit internal
                  notes/files). Offline suppliers stay fully editable. */}
              {supSettings?.user_id ? (
                <>
                  {(supSettings?.yachts || []).length > 0 && (
                    <Button
                      variant="outline"
                      onClick={() => { const s = supSettings; setSupSettings(null); messageSupplier(s); }}
                      data-testid="supplier-message-btn"
                      title={`Message ${supSettings.contact_person || supSettings.name}`}
                    >
                      <MessageSquare className="h-4 w-4 mr-1.5" /> Message
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => { const s = supSettings; setSupSettings(null); openEditSupplier(s, true); }}
                    data-testid="supplier-show-details"
                    title="View the company-maintained details (read-only) and edit internal notes/files"
                  >
                    Show Details
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => { const s = supSettings; setSupSettings(null); openEditSupplier(s); }}
                  data-testid="supplier-edit"
                >
                  Show details
                </Button>
              )}
              <Button
                onClick={() => setSupSettings(null)}
                className="bg-ink hover:bg-black"
                data-testid="supplier-settings-close"
              >
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title || ""}
        description={confirm?.description}
        confirmLabel={confirm?.confirmLabel}
        destructive={confirm?.destructive}
        busy={confirmBusy}
        onCancel={() => { if (!confirmBusy) setConfirm(null); }}
        onConfirm={runConfirm}
        testid="network-confirm-dialog"
      />

      {/* Supplier "Add to yacht" modal — calls /suppliers/{id}/assign so the
          supplier is added as OFFLINE. The user explicitly opts into the
          invite flow from the supplier profile when they're ready. */}
      <Dialog open={!!supplierAssignTarget} onOpenChange={(o) => { if (!o) closeSupplierAssign(); }}>
        <DialogContent className="max-w-md" data-testid="supplier-assign-modal">
          <DialogHeader>
            <DialogTitle>Add {supplierAssignTarget?.name} to a yacht</DialogTitle>
            <DialogDescription>
              They'll be added to the selected yacht as an <strong>offline supplier</strong>. No email is sent.
              You can promote them to a linked Online Supplier anytime via the supplier profile's
              "Send invite" button.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-zinc-600">Yacht</label>
              <select
                className="mt-1 h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                value={supplierAssignYacht}
                onChange={(e) => setSupplierAssignYacht(e.target.value)}
                data-testid="supplier-assign-yacht"
              >
                {supplierAssignableForTarget.map((y) => (
                  <option key={y.id} value={y.id}>{y.name}</option>
                ))}
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeSupplierAssign} data-testid="supplier-assign-cancel">Cancel</Button>
            <Button
              onClick={submitSupplierAssign}
              disabled={supplierAssignBusy || !supplierAssignYacht}
              className="bg-ink hover:bg-black"
              data-testid="supplier-assign-confirm"
            >
              {supplierAssignBusy ? "Adding…" : "Add to yacht"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Crew Info modal — read-only details. The only mutation here is the
          "Remove from my network" action, gated to people with no shared
          yacht (per the user's "delete only when unassigned" rule). */}
      <Dialog open={!!crewInfo} onOpenChange={(o) => { if (!o && !crewInfoBusy) setCrewInfo(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="crew-info-modal">
          <DialogHeader>
            <DialogTitle>
              {crewInfo?.role === "provider" && crewInfo?.company ? crewInfo.company : (crewInfo?.name || crewInfo?.email)}
            </DialogTitle>
            <DialogDescription>
              {ROLE_LABELS[crewInfo?.role] || crewInfo?.role}
            </DialogDescription>
          </DialogHeader>

          {crewInfo && (
            <div className="space-y-3 text-sm">
              {crewInfo.role === "provider" && crewInfo.company && crewInfo.name && (
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500 w-24 shrink-0">Contact</span>
                  <span className="font-medium">{crewInfo.name}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Mail className="h-3.5 w-3.5 text-zinc-400" />
                <span className="text-zinc-500 w-20 shrink-0">Email</span>
                <span className="truncate">{crewInfo.email}</span>
              </div>
              {crewInfo.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-3.5 w-3.5 text-zinc-400" />
                  <span className="text-zinc-500 w-20 shrink-0">Phone</span>
                  <span className="truncate">{crewInfo.phone}</span>
                </div>
              )}
              {crewInfo.company && crewInfo.role !== "provider" && (
                <div className="flex items-center gap-2">
                  <Building2 className="h-3.5 w-3.5 text-zinc-400" />
                  <span className="text-zinc-500 w-20 shrink-0">Company</span>
                  <span className="truncate">{crewInfo.company}</span>
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1 mt-2">Shared yachts</div>
                {(crewInfo.yachts || []).length === 0 ? (
                  <div className="text-zinc-400 text-xs">Not on any yacht with you currently.</div>
                ) : (
                  <ul className="space-y-1" data-testid="crew-info-yachts">
                    {crewInfo.yachts.map((y) => (
                      <li key={y} className="flex items-center gap-2 text-sm">
                        <Ship className="h-3.5 w-3.5 text-zinc-400" />
                        <span>{y}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Business details — read-only by default, switches to
                  editable when the senior caller hits "Edit". The
                  backend gate (owner/manager only) decides if save
                  succeeds; failures surface as a toast. */}
              {crewEdit ? (
                <div data-testid="crew-business-edit" className="rounded-md border border-zinc-200 bg-zinc-50/60 p-3 space-y-3">
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Business details</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <SupBizField label="Website" testid="crew-website"
                      value={crewEdit.website} placeholder="https://"
                      onChange={(v) => setCrewEdit({ ...crewEdit, website: v })} />
                    <SupBizField label="VAT / Tax ID" testid="crew-vat-id"
                      value={crewEdit.vat_id}
                      onChange={(v) => setCrewEdit({ ...crewEdit, vat_id: v })} />
                    <SupBizField label="Tax number (other)" testid="crew-tax-id"
                      value={crewEdit.tax_id}
                      onChange={(v) => setCrewEdit({ ...crewEdit, tax_id: v })} />
                  </div>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500 pt-1">Address</div>
                  <SupBizField label="Street (line 1)" testid="crew-addr1"
                    value={crewEdit.address_line1}
                    onChange={(v) => setCrewEdit({ ...crewEdit, address_line1: v })} />
                  <SupBizField label="Street (line 2)" testid="crew-addr2"
                    value={crewEdit.address_line2}
                    onChange={(v) => setCrewEdit({ ...crewEdit, address_line2: v })} />
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <SupBizField label="City" testid="crew-city"
                      value={crewEdit.city}
                      onChange={(v) => setCrewEdit({ ...crewEdit, city: v })} />
                    <SupBizField label="Postcode" testid="crew-postcode"
                      value={crewEdit.postcode}
                      onChange={(v) => setCrewEdit({ ...crewEdit, postcode: v })} />
                    <SupBizField label="Country" testid="crew-country"
                      value={crewEdit.country}
                      onChange={(v) => setCrewEdit({ ...crewEdit, country: v })} />
                  </div>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500 pt-1">Bank</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <SupBizField label="Bank name" testid="crew-bank-name"
                      value={crewEdit.bank_name}
                      onChange={(v) => setCrewEdit({ ...crewEdit, bank_name: v })} />
                    <SupBizField label="Account holder" testid="crew-account-holder"
                      value={crewEdit.account_holder}
                      onChange={(v) => setCrewEdit({ ...crewEdit, account_holder: v })} />
                    <SupBizField label="IBAN" testid="crew-iban"
                      value={crewEdit.iban}
                      onChange={(v) => setCrewEdit({ ...crewEdit, iban: v })} />
                    <SupBizField label="BIC / SWIFT" testid="crew-bic-swift"
                      value={crewEdit.bic_swift}
                      onChange={(v) => setCrewEdit({ ...crewEdit, bic_swift: v })} />
                  </div>
                  <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" size="sm" onClick={() => setCrewEdit(null)} disabled={crewEditBusy} data-testid="crew-edit-cancel">Cancel</Button>
                    <Button size="sm" onClick={saveCrewEdit} disabled={crewEditBusy} className="bg-ink hover:bg-black" data-testid="crew-edit-save">
                      {crewEditBusy ? "Saving…" : "Save"}
                    </Button>
                  </div>
                </div>
              ) : crewInfo.business && (
                <div data-testid="crew-business">
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1 mt-2">Business details</div>
                  <div className="rounded-md border border-zinc-200 bg-zinc-50/60 p-3 space-y-1.5 text-sm">
                    {crewInfo.business.website && (
                      <BizRow k="Website" v={(
                        <a
                          href={/^https?:\/\//i.test(crewInfo.business.website)
                            ? crewInfo.business.website
                            : `https://${crewInfo.business.website}`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-ink underline underline-offset-2 hover:text-black break-all"
                        >{crewInfo.business.website}</a>
                      )} />
                    )}
                    {crewInfo.business.account_holder && <BizRow k="Account holder" v={crewInfo.business.account_holder} />}
                    {crewInfo.business.bank_name && <BizRow k="Bank" v={crewInfo.business.bank_name} />}
                    {crewInfo.business.iban && <BizRow k="IBAN" v={crewInfo.business.iban} mono copyable />}
                    {crewInfo.business.bic_swift && <BizRow k="BIC / SWIFT" v={crewInfo.business.bic_swift} mono copyable />}
                    {crewInfo.business.vat_id && <BizRow k="VAT ID" v={crewInfo.business.vat_id} copyable />}
                    {crewInfo.business.tax_id && <BizRow k="Tax ID" v={crewInfo.business.tax_id} />}
                    {(crewInfo.business.address_line1 || crewInfo.business.city || crewInfo.business.country) && (
                      <BizAddress addr={crewInfo.business} />
                    )}
                  </div>
                </div>
              )}

              {/* Files attached to this crew member. Listed inside the
                  Info modal (read-only here). Upload/delete happens
                  inline when business-edit mode is active. */}
              <div className="pt-1">
                <AttachmentsPanel
                  kind="crew_member"
                  refId={crewInfo.id}
                  editable={!!crewEdit}
                  title="Files (PDFs & photos)"
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex sm:justify-between gap-2">
            {(!crewInfo?.yachts || crewInfo.yachts.length === 0) ? (
              <Button
                variant="ghost"
                onClick={removeCrewFromNetworkInline}
                className="text-red-600 hover:bg-red-50"
                data-testid="crew-info-remove"
              >
                <Trash2 className="h-4 w-4 mr-1.5" /> Remove from my network
              </Button>
            ) : <div />}
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={refreshCrewInfo}
                disabled={crewInfoBusy}
                data-testid="crew-info-refresh"
                title="Pull the latest profile data from this user"
              >
                {crewInfoBusy ? "Refreshing…" : "Refresh from user"}
              </Button>
              <Button
                onClick={() => setCrewInfo(null)}
                className="bg-ink hover:bg-black"
                data-testid="crew-info-close"
              >
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

